import React from 'react';

const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

export interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'secondary' | 'gray';
  className?: string;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'primary',
  className,
}) => {
  const dotSizes = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5',
    xl: 'w-3 h-3',
  };

  const gapSizes = {
    sm: 'gap-1',
    md: 'gap-1.5',
    lg: 'gap-2',
    xl: 'gap-2.5',
  };

  const colorClasses = {
    primary: 'bg-[#13EC5B]',
    secondary: 'bg-blue-400',
    gray: 'bg-gray-400',
  };

  return (
    <div className={cn('inline-flex items-center', gapSizes[size], className)}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={cn('rounded-full animate-[dotBounce_1.4s_ease-in-out_infinite]', dotSizes[size], colorClasses[color])}
          style={{ animationDelay: `${i * 0.16}s` }}
        />
      ))}
      <style>{`
        @keyframes dotBounce {
          0%, 80%, 100% { transform: scale(0.4); opacity: 0.3; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export interface LoadingPageProps {
  message?: string;
  className?: string;
}

const LoadingPage: React.FC<LoadingPageProps> = ({
  message = 'Loading...',
  className,
}) => {
  return (
    <div className={cn(
      'flex flex-col items-center justify-center min-h-screen bg-[#0F1114]',
      className
    )}>
      <div className="text-center space-y-4">
        <LoadingSpinner size="xl" />
        <p className="text-gray-400 text-body-medium">{message}</p>
      </div>
    </div>
  );
};

export interface LoadingSectionProps {
  message?: string;
  height?: string;
  className?: string;
}

const LoadingSection: React.FC<LoadingSectionProps> = ({
  message = 'Loading...',
  height = 'h-64',
  className,
}) => {
  return (
    <div className={cn(
      'flex flex-col items-center justify-center',
      height,
      className
    )}>
      <div className="text-center space-y-3">
        <LoadingSpinner size="lg" />
        <p className="text-gray-400 text-body-small">{message}</p>
      </div>
    </div>
  );
};

export interface LoadingSkeletonProps {
  className?: string;
  variant?: 'text' | 'rectangular' | 'circular';
  width?: string;
  height?: string;
}

const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  className,
  variant = 'rectangular',
  width = 'w-full',
  height = 'h-4',
}) => {
  const variantClasses = {
    text: 'rounded',
    rectangular: 'rounded-md',
    circular: 'rounded-full',
  };

  return (
    <div
      className={cn(
        'animate-pulse bg-white/[0.05]',
        variantClasses[variant],
        width,
        height,
        className
      )}
    />
  );
};

export { LoadingSpinner, LoadingPage, LoadingSection, LoadingSkeleton };
